// TODO: Migration ot translatedCopy.js of raw strings
// TODO: Using generative voice service to enable generative at mount

import React, { useState, useEffect, useRef, useMemo } from 'react';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton2/AmiraIconButton2';
import ArrowIcon from '../Global/Icons/ArrowIcon';
import cx from 'classnames';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import { PhraseTriggers } from '../../constants/constants';

const IDLE_ENCOURAGEMENT_TIMEOUT = 15000;
const IDLE_SUBMIT_TIMEOUT = 60000;

const Spelling = ({
  speech,
  word,
  letterDisplay,
  isInstruction,
  isRepeating,
  handleRepeatInstructions,
  repeatInstructions,
  setTextResponse,
  setHideParentRepeatButton,
  currentIntroductionTrigger,
  useTyping
}) => {

  // State
  const [inputValue, setInputValue] = useState("");
  const [availableLetters, setAvailableLetters] = useState([]);
  const [inFTUE, setInFTUE] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hideLetters, setHideLetters] = useState(false);
  const [hideSubmit, setHideSubmit] = useState(false);
  const [hideInputControls, setHideInputControls] = useState(false);

  const idleEncouragementText = useTyping
    ? `You can do it. Type your answer in the box. The word is: ${repeatInstructions}`
    : `You can do it. Click the letters to input your answer. The word is: ${repeatInstructions}`;
    
  const { resetIdleTimer, clearIdleTimer, idleSpeechPlaying, idleTimeoutExpired } = useIdleTimer(
    speech, 
    IDLE_ENCOURAGEMENT_TIMEOUT, 
    IDLE_SUBMIT_TIMEOUT
  );

  // Memos
  const disableAll = useMemo(() => {
    return (isInstruction || isRepeating || loading || idleSpeechPlaying);
  }, [isInstruction, isRepeating, loading, idleSpeechPlaying]);

  const disableSubmit = useMemo(() => {
    return (disableAll || inputValue.length < 1) && currentIntroductionTrigger !== PhraseTriggers.Submit;
  }, [disableAll, inFTUE, inputValue, currentIntroductionTrigger]);

  // Refs
  const inputElement = useRef(null);

  // Handlers
  const handleLetterClick = (letter) => {
    if (inFTUE || disableAll || useTyping) return;
    const newValue = inputValue + letter;
    setInputValue(newValue);
    resetIdleTimer(idleEncouragementText);
  };

  const handleTyping = (event) => {
    if (inFTUE || disableAll || !useTyping) return;
    setInputValue(event.target.value);
    resetIdleTimer(idleEncouragementText);
  };

  const handleBackspace = () => {
    if (inFTUE || disableAll) return;
    if (inputValue.length > 0) {
      const newValue = inputValue.slice(0, -1);
      setInputValue(newValue);
      resetIdleTimer(idleEncouragementText);
    }

    if (inputElement && inputElement.current) {
      inputElement.current.focus();
    }
  };

  const handleSubmit = async (forceSubmit) => {
    if ((inFTUE || disableSubmit) && !forceSubmit) return;
    if ((inputValue && inputValue !== "") || forceSubmit) {
      clearIdleTimer();
      speech.abortSpeech();
      setLoading(true);

      if (forceSubmit) {
        // If use was idle, we want to speech to notify them we are moving on.
        await speech.speakWithPromise({
          textToSpeak: "Let's move on."
        });
      }

      // true signals this is the final version of the response
      setTextResponse((inputValue || "").toLowerCase(), true);
    }
  };

  const handleTrigger = (trigger) => {
    switch (trigger) {
      case PhraseTriggers.Start:
        setInFTUE(true);
        setHideInputControls(false);
        setTimeout(() => {
          setHideLetters(false);
          setHideSubmit(false);
        }, 2000);
        break;
      case PhraseTriggers.Repeat:
      case PhraseTriggers.Letters:
      case PhraseTriggers.Backspace:
      case PhraseTriggers.Submit:
        break;
      case PhraseTriggers.Ready:
        setInFTUE(false);
        break;
      default:
        break;
    }
  };

  // Effects
  useEffect(() => {
    // TODO: Handle this only in Phrase, not here
    if (setHideParentRepeatButton) {
      setHideParentRepeatButton(true);
    }

    return () => {
      if (setHideParentRepeatButton) {
        setHideParentRepeatButton(false);
      }
    }
  }, []);

  useEffect(() => {
    if (currentIntroductionTrigger) {
      handleTrigger(currentIntroductionTrigger);
    }
  }, [currentIntroductionTrigger]);

  useEffect(() => {
    if (inputElement && inputElement.current) {
      const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !disableSubmit) {
          handleSubmit();
        }
        if (!useTyping) {
          event.preventDefault(); // Prevent actual typing only if useTyping is false
        }
      };

      inputElement.current.addEventListener("keydown", handleKeyDown);

      return () => {
        if (inputElement && inputElement.current) {
          inputElement.current.removeEventListener("keydown", handleKeyDown);
        }
      };
    }
  }, [disableSubmit, isInstruction, inputElement, useTyping]);

  useEffect(() => {
    if (idleTimeoutExpired) {
      const forceSubmit = true;
      handleSubmit(forceSubmit);
    }
  }, [idleTimeoutExpired]);

  useEffect(() => {
    if (inputElement && inputElement.current && !isInstruction && !isRepeating) {
      setTimeout(() => {
        inputElement.current.focus();
      }, 50);
      setLoading(false);
      resetIdleTimer(idleEncouragementText);
    }
  }, [isInstruction, isRepeating]);

  // When word changes, update the letter display for non-typing users
  useEffect(() => {
    setInFTUE(false);
    if (!useTyping) {
      if (word) {
        // Remove duplicates
        setAvailableLetters([...new Set(letterDisplay.split(''))]);
      } else {
        setAvailableLetters([]);
      }
    }
  }, [word]);

  useEffect(() => {
    if (inputValue.length > 0) {
      setTextResponse(inputValue.toLowerCase());
    }
  }, [inputValue]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--letters-count', availableLetters.length);
  }, [availableLetters]);

  return (
    <div className={`spellingContainer animate ${useTyping ? '' : 'fullscreen'}`}>
      <div className={`inputControlsContainer ${hideInputControls ? 'hidden' : ''}`}>
        <AmiraIconButton
          type={ICON_BUTTON_TYPES.REPEAT_AUDIO}
          secondary
          onClick={() => {
            if (!inFTUE && !disableAll) {
              clearIdleTimer();
              handleRepeatInstructions();
            }
          }}
          disabled={disableAll && currentIntroductionTrigger !== PhraseTriggers.Repeat}
          pointerPosition="left"
          showPointer={currentIntroductionTrigger === PhraseTriggers.Repeat}
        />
        <input
          ref={inputElement}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          type="text"
          placeholder=""
          value={inputValue}
          readOnly={!useTyping}
          className={cx(`styledInput pointerTarget`, {
            showPointer: currentIntroductionTrigger === PhraseTriggers.Letters && useTyping,
            sweep: currentIntroductionTrigger === PhraseTriggers.Letters && useTyping,
            disabled: disableAll && !(useTyping && currentIntroductionTrigger === PhraseTriggers.Letters)
          })}
          disabled={disableAll && !(useTyping && currentIntroductionTrigger === PhraseTriggers.Letters)}
          onChange={(event) => {
            handleTyping(event);
          }}
          onContextMenu={(event) => {
            event.preventDefault(); // Disable right-click menu
          }}
        />
        <AmiraIconButton
          type={ICON_BUTTON_TYPES.BACKSPACE}
          secondary
          onClick={handleBackspace}
          disabled={disableAll && currentIntroductionTrigger !== PhraseTriggers.Backspace}
          pointerPosition="left"
          showPointer={currentIntroductionTrigger === PhraseTriggers.Backspace}
        />
      </div>
      <div className={cx(`lettersContainer`, {
        disabled: disableAll && currentIntroductionTrigger !== PhraseTriggers.Letters,
        hidden: useTyping || hideLetters
      })}>
        <div
          className={cx(`pointerTarget`, {
            showPointer: currentIntroductionTrigger === PhraseTriggers.Letters && !useTyping,
            sweep: currentIntroductionTrigger === PhraseTriggers.Letters && !useTyping
          })}
          style={{ width: '100%', display: 'flex' }}
        >
          {availableLetters.map((letter, index) => (
            <span
              key={index}
              className="letter"
              onClick={() => {
                if (!isInstruction && !isRepeating && !inFTUE) {
                  handleLetterClick(letter, index);
                }
              }}
              style={{ '--letter-index': index }}
            >
              {letter}
            </span>
          ))}
        </div>
      </div>
      <AmiraIconButton
        type={ICON_BUTTON_TYPES.SUBMIT}
        oblong
        small
        onClick={() => handleSubmit()}
        disabled={disableSubmit}
        invisible={hideSubmit}
        pointerPosition="left"
        showPointer={currentIntroductionTrigger === PhraseTriggers.Submit}
        assessment={true}
      />
    </div>
  );
};

export default Spelling;